import React from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as OrdersIcon,
  Schedule as ScheduleIcon,
} from "@material-ui/icons";
import { usePdvStyles } from "../styles/pdvStyles";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

const formatPhone = (phone) => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const formatCPF = (cpf) => {
  if (!cpf) return "";
  const cleaned = cpf.replace(/\D/g, "");
  if (cleaned.length === 11) {
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
  }
  return cpf;
};

const formatCNPJ = (cnpj) => {
  if (!cnpj) return "";
  const cleaned = cnpj.replace(/\D/g, "");
  if (cleaned.length === 14) {
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12)}`;
  }
  return cnpj;
};

const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("pt-BR");
};

const formatDaysSince = (days) => {
  if (days === null || days === undefined) return "";
  if (days === 0) return "hoje";
  if (days === 1) return "ontem";
  if (days < 7) return `${days} dias`;
  if (days < 30) return `${Math.floor(days / 7)} sem`;
  if (days < 365) return `${Math.floor(days / 30)} mês`;
  return `${Math.floor(days / 365)} ano`;
};

const PAYMENT_LABELS = {
  money: "Dinheiro",
  credit: "Crédito",
  debit: "Débito",
  pix: "PIX",
};

const PdvCustomerSummary = ({
  contact,
  contactLoading = false,
  customerStats = {},
  statsLoading = false,
  ticketId,
  onIdentifyClick,
  onEditClick,
}) => {
  const classes = usePdvStyles();
  const {
    lastOrder,
    averageTicket,
    totalOrders,
    totalSpent,
    daysSinceLastOrder,
    favoritePaymentMethod,
    hasAddress,
    isIdentified: statsIdentified,
  } = customerStats || {};

  const isIdentified = contact && (contact.cpf || contact.cnpj || contact.name !== contact.number);
  const customerName = contact?.name || contact?.number || "Cliente não identificado";
  const document = contact?.cpf ? formatCPF(contact.cpf) : contact?.cnpj ? formatCNPJ(contact.cnpj) : null;
  const documentLabel = contact?.cpf ? "CPF" : contact?.cnpj ? "CNPJ" : null;
  const phone = formatPhone(contact?.number);

  // Build address string
  const addressParts = [];
  if (contact?.street) addressParts.push(contact.street);
  if (contact?.streetNumber) addressParts.push(contact.streetNumber);
  if (contact?.complement) addressParts.push(contact.complement);
  if (contact?.neighborhood) addressParts.push(contact.neighborhood);
  const address = addressParts.join(", ");

  const hasStats = totalOrders > 0;

  // Show loading state while contact is being fetched, OR when no contact yet
  // This prevents the flickering effect
  if (contactLoading || !contact) {
    return (
      <Box
        className={classes.customerSummary}
        style={{
          backgroundColor: '#f5f5f5',
          borderLeft: '3px solid #9e9e9e',
        }}
      >
        <Box className={classes.customerSummaryHeader}>
          <Box className={classes.customerName}>
            <CircularProgress size={16} style={{ marginRight: 8 }} />
            <Typography component="span" style={{ color: '#666' }}>
              Carregando cliente...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={classes.customerSummary}
      onClick={onIdentifyClick}
      style={{
        backgroundColor: isIdentified ? '#e8f5e9' : '#fff8e1',
        borderLeft: isIdentified ? '3px solid #4caf50' : '3px solid #ffc107',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = isIdentified ? '#c8e6c9' : '#ffecb3';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isIdentified ? '#e8f5e9' : '#fff8e1';
      }}
    >
      <Box className={classes.customerSummaryHeader}>
        <Box className={classes.customerName}>
          <PersonIcon fontSize="small" />
          <Typography component="span">
            {isIdentified ? (
              customerName
            ) : (
              <span className={classes.customerNotIdentified}>
                <WarningIcon fontSize="inherit" style={{ marginRight: 4, verticalAlign: "middle" }} />
                Cliente não identificado
              </span>
            )}
          </Typography>
          {contact?.personType && (
            <Chip
              size="small"
              label={contact.personType}
              variant="outlined"
              color={contact.personType === "PJ" ? "primary" : "default"}
            />
          )}
        </Box>
        
      </Box>

      <Box className={classes.customerDetails}>
        {phone && (
          <Box className={classes.customerDetailItem}>
            <PhoneIcon />
            <span>{phone}</span>
          </Box>
        )}
      </Box>

      {/* Stats section */}
      {statsLoading ? (
        <Box className={classes.customerStats} style={{ justifyContent: "center" }}>
          <CircularProgress size={20} />
        </Box>
      ) : hasStats && (
        <Box className={classes.customerStats}>
          {/* Total orders */}
          <Tooltip title={`Total gasto: ${formatCurrency(totalSpent)}`}>
            <Box className={classes.customerStatItem}>
              <span className="label">Pedidos</span>
              <span className="value">
                <OrdersIcon fontSize="inherit" style={{ marginRight: 4, verticalAlign: "middle" }} />
                {totalOrders}
              </span>
            </Box>
          </Tooltip>

          {/* Average ticket */}
          {averageTicket !== null && averageTicket !== undefined && (
            <Box className={classes.customerStatItem}>
              <span className="label">Ticket Médio</span>
              <span className="value">
                <TrendingUpIcon fontSize="inherit" style={{ marginRight: 4, verticalAlign: "middle" }} />
                {formatCurrency(averageTicket)}
              </span>
            </Box>
          )}

          {/* Last order */}
          {lastOrder && (
            <Tooltip title={`Último pedido em ${formatDate(lastOrder.date)}`}>
              <Box className={classes.customerStatItem}>
                <span className="label">Último Pedido</span>
                <span className="value">
                  <ReceiptIcon fontSize="inherit" style={{ marginRight: 4, verticalAlign: "middle" }} />
                  {formatCurrency(lastOrder.total)}
                </span>
              </Box>
            </Tooltip>
          )}

          {/* Days since last order */}
          {daysSinceLastOrder !== null && daysSinceLastOrder !== undefined && (
            <Tooltip title={`${daysSinceLastOrder} dias desde o último pedido`}>
              <Box className={classes.customerStatItem}>
                <span className="label">Última Compra</span>
                <span className="value">
                  <ScheduleIcon fontSize="inherit" style={{ marginRight: 4, verticalAlign: "middle" }} />
                  {formatDaysSince(daysSinceLastOrder)}
                </span>
              </Box>
            </Tooltip>
          )}

          {/* Favorite payment method */}
          {favoritePaymentMethod && (
            <Box className={classes.customerStatItem}>
              <span className="label">Pref. Pgto</span>
              <span className="value" style={{ textTransform: "capitalize" }}>
                {PAYMENT_LABELS[favoritePaymentMethod] || favoritePaymentMethod}
              </span>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(PdvCustomerSummary);
